import {
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import BoxDX from "../../layout/boxdx";
import GridDX from "../../layout/griddx";
import TypographyDX from "../../layout/typographydx";
import ButtonDX from "../../controls/buttondx";
import { useTranslation } from "react-i18next";
import { useEffect, useRef, useState } from "react";
import { read, utils } from "xlsx";
import { useNotificationContext } from "../../../context/notificationcontext";
import LoadingButtonDX from "../../controls/loadingbuttondx";
import TextFieldDX from "../../controls/textfielddx";
import SelectListDX from "../../controls/selectlistdx";
import useWarehouseService from "../../../shared/services/warehouseservice";
import useWarehouseCarrierService from "../../../shared/services/warehousecarrierservice";
import { useAuthContext } from "../../../context/authcontext";
import { useTenantContext } from "../../../context/tenantcontext";
import useWorldService from "../../../shared/services/worldservice";

const CarrierBulkForm = (props: any) => {
  const { t, i18n } = useTranslation();
  const { userData } = useAuthContext();
  const { tenantId } = useTenantContext();
  const { setInfo, setError } = useNotificationContext();
  const { getCitiesByCountryId } = useWorldService();
  const { getWarehouses } = useWarehouseService();
  const { addWarehouseCarriersInBulk } = useWarehouseCarrierService();

  const inputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<null | File>(null);
  const [cities, setCities] = useState<any>([]);
  const [warehouses, setWarehouses] = useState([]);
  const [warehouseId, setWarehouseId] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState("");

  const handleClick = (event: any) => {
    if (inputRef.current != null) {
      inputRef.current.click();
    }
  };

  const handleDragOver = (event: any) => {
    event.preventDefault();
    if (event.target.files) setFile(event.target.files[0]);
  };

  const handleDrop = async (event: any) => {
    event.preventDefault();
    if (event.target.files) setFile(event.target.files[0]);
  };

  const onChangeHandler = async (event: any) => {
    if (event.target.files) setFile(event.target.files[0]);
  };

  const onSave = async () => {
    if (!file) {
      setError(t("Please select a file"));
      return;
    }

    setIsSaving(true);
    const ab = await file?.arrayBuffer();
    const wb = read(ab); //parse data
    const ws = wb.Sheets[wb.SheetNames[0]]; // get the first worksheet
    const readData = utils.sheet_to_json(ws); // generate objects
    let data: any = [];
    readData.forEach((item: any) => {
      const { City, IntegrationType, ...rest } = item;
      const id = getCityId(City);
      const type =
        IntegrationType.toLowerCase() !== "no"
          ? IntegrationType.toLowerCase() === "wms"
            ? 3
            : 2
          : 1;
      if (id)
        data.push({
          ...rest,
          cityId: id,
          warehouseId: warehouseId,
          integrationMethodTypeId: type,
          enableZebraPrint:
            (item.enableZebraPrint ?? "").toLowerCase() === "yes",
          enableMultiPrint:
            (item.enableMultiPrint ?? "").toLowerCase() === "yes",
          enableReturnSupport:
            (item.enableReturnSupport ?? "").toLowerCase() === "yes",
          enableMultiparcelSupport:
            (item.enableMultiparcelSupport ?? "").toLowerCase() === "yes",
          carrierSupported:
            (item.carrierSupported ?? "").toLowerCase() === "yes",
        });
      else setErrors("One or more rows had unknown city names");
    });

    const operation = addWarehouseCarriersInBulk(data);

    operation
      .then((res) => {
        setInfo(t("Order bins added successfully"));
        props.handleClose(true);
      })
      .catch((err) => setError(err))
      .finally(() => setIsSaving(false));
  };

  const getCityId = (name: string) => {
    for (let city of cities) {
      if (city.cityName.toLowerCase() === name.toLowerCase())
        return city.cityId;
    }
    return null;
  };

  const getData = async () => {
    const p1 =
      userData?.userType === "S" ? getWarehouses(tenantId) : getWarehouses();
    const p2 = getCitiesByCountryId();

    setIsLoading(true);
    Promise.all([p1, p2])
      .then(([warehouseList, cityList]) => {
        setWarehouses(
          warehouseList.map((item: any) => {
            return { text: item.name, value: item.warehouseId };
          }),
        );
        setCities(cityList);
      })
      .catch((err) => setError(err))
      .finally(() => setIsLoading(false));
  };

  useEffect(() => {
    getData();
  }, [tenantId]);

  return (
    <Dialog
      open={props.open}
      onClose={() => props.handleClose()}
      maxWidth="md"
      fullWidth
      disableEscapeKeyDown
      sx={{ direction: i18n.dir(), ...props.sx }}
    >
      <DialogTitle
        display={"flex"}
        flexDirection={"row"}
        justifyContent={"space-between"}
      >
        <TypographyDX variant="h5" fontWeight="bold">
          {t("Carriers Import")}
        </TypographyDX>
        <GridDX item xs={12} md={4}>
          <SelectListDX
            disabled={isLoading}
            label={t("Select Warehouse")}
            name="warehouseId"
            items={warehouses}
            value={warehouseId}
            onChange={(e: any) => setWarehouseId(e.target.value)}
          />
        </GridDX>
      </DialogTitle>
      <DialogContent>
        <GridDX container columnSpacing={1} rowSpacing={2} sx={{ py: 1 }}>
          {errors === "" ? (
            <>
              <GridDX
                item
                xs={12}
                direction="column"
                justifyContent="center"
                alignItems="center"
                sx={{ mx: 2, height: "100%" }}
              >
                {file ? (
                  <BoxDX display="flex" sx={{ width: 600 }}>
                    <Chip
                      label={file.name}
                      color="primary"
                      onDelete={() => setFile(null)}
                    />
                  </BoxDX>
                ) : (
                  <BoxDX
                    onDragOver={handleDragOver}
                    onDrop={handleDrop}
                    component="fieldset"
                    sx={{
                      border: "1px dashed #c6c6c6",
                      borderRadius: 1,
                      width: 600,
                      height: 150,
                    }}
                  >
                    <BoxDX
                      display="flex"
                      flexDirection="column"
                      justifyContent="center"
                      alignItems="center"
                      direction="column"
                    >
                      <Typography
                        sx={{
                          fontSize: 18,
                          textAlign: "center",
                        }}
                      >
                        {t("Drag and Drop File here or")}
                      </Typography>
                      <ButtonDX variant="text" onClick={handleClick}>
                        {t("Browse")}
                      </ButtonDX>
                    </BoxDX>
                    <BoxDX sx={{ display: "none" }} justifyContent="center">
                      <input
                        ref={inputRef}
                        accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                        type="file"
                        name="file"
                        style={{
                          display: "none",
                        }}
                        onChange={onChangeHandler}
                      />
                    </BoxDX>
                  </BoxDX>
                )}
              </GridDX>
            </>
          ) : (
            <GridDX
              item
              xs={12}
              direction="column"
              justifyContent="center"
              alignItems="center"
              sx={{ mx: 2, height: "100%" }}
            >
              <BoxDX
                display="flex"
                sx={{ width: 600, justifyContent: "space-between" }}
              >
                <TextFieldDX
                  name="errors"
                  label={t("Errors")}
                  value={errors}
                  multiline
                  readOnly
                />
              </BoxDX>
            </GridDX>
          )}
        </GridDX>
      </DialogContent>
      <DialogActions
        sx={{ display: "flex", flexDirection: "column", alignItems: "end" }}
      >
        <BoxDX display="flex" flexDirection="row">
          {errors === "" ? (
            <>
              <ButtonDX
                variant="outlined"
                sx={{ mx: 2, mb: { xs: 2, sm: "auto" }, width: 120 }}
                onClick={() => props.handleClose()}
              >
                {t("Cancel")}
              </ButtonDX>
              <LoadingButtonDX
                onClick={onSave}
                sx={{ width: 120 }}
                loading={isSaving}
                loadingPosition="end"
              >
                {t("Save")}
              </LoadingButtonDX>
            </>
          ) : (
            <ButtonDX
              disabled={isSaving}
              onClick={() => props.handleClose(true)}
            >
              {t("Close")}
            </ButtonDX>
          )}
        </BoxDX>
      </DialogActions>
    </Dialog>
  );
};

export default CarrierBulkForm;
